import { useApp } from "../../context/AppContext.js";
import { useDashboardTotals } from "../../hooks/useDashboardTotals.js";
import { fmt } from "../../utils/formatters.js";
import { fmtDateShort, toDateInput, parseDate } from "../../utils/dateHelpers.js";
import { canInvoiceClient } from "../../utils/billing.js";

export function Dashboard() {
  const {
    clients, entries, rangeStart, rangeEnd,
    setActiveTab, setInvoiceDraft,
  } = useApp();

  const activeClients = clients.filter((c) => c.status === "active");
  const {
    byClient, grandSubtotal, grandGST, grandTotal,
    totalHours, totalHourlyCharges, totalTaskCharges, totalFixedCharges,
  } = useDashboardTotals(activeClients, entries, rangeStart, rangeEnd);

  const rangeStartDate = rangeStart ? parseDate(rangeStart) : new Date(0);
  const rangeEndDate = rangeEnd ? parseDate(rangeEnd) : new Date(9999, 11, 31);

  const handleGenerateInvoice = (clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    if (!client) return;
    const cEntries = byClient[clientId]?.entries ?? [];
    const invNum = `INV-${(rangeStart || rangeEnd || toDateInput(new Date())).slice(0, 7)}-${client.name.split(" ").map((w) => w[0]).join("").toUpperCase()}`;

    setActiveTab("dashboard");
    setInvoiceDraft({ client, entries: cEntries, startDate: rangeStart, endDate: rangeEnd, invoiceNumber: invNum });
  };

  const rangeLabel =
    rangeStart || rangeEnd
      ? `${rangeStart ? fmtDateShort(rangeStart) : ""}${rangeStart && rangeEnd ? " – " : ""}${rangeEnd ? fmtDateShort(rangeEnd) : ""}`
      : "All Time";

  return (
    <div>
      {/* Stat cards */}
      <div className="flex-row" style={{ marginBottom: 24 }}>
        <div className="card stat-card">
          <div className="stat-label">Subtotal</div>
          <div className="stat-value">{fmt(grandSubtotal)}</div>
        </div>
        <div className="card stat-card">
          <div className="stat-label">GST (5%)</div>
          <div className="stat-value gst-value">{fmt(grandGST)}</div>
        </div>
        <div className="card stat-card">
          <div className="stat-label">Total</div>
          <div className="stat-value">{fmt(grandTotal)}</div>
        </div>
        <div className="card stat-card">
          <div className="stat-label">Total Hours</div>
          <div className="stat-value">{totalHours.toFixed(1)}</div>
        </div>
        <div className="card stat-card">
          <div className="stat-label">Active Clients</div>
          <div className="stat-value">{activeClients.length}</div>
        </div>
      </div>

      {/* Client breakdown table */}
      <div className="card">
        <h3 className="section-title">Client Breakdown — {rangeLabel}</h3>
        <table>
          <thead>
            <tr>
              {["Client", "Type", "Hours", "Hourly", "Task $", "Fixed", "Subtotal", "GST (5%)", "Total", ""].map(
                (h, i) => <th key={i} className={i > 1 ? "right" : ""}>{h}</th>,
              )}
            </tr>
          </thead>
          <tbody>
            {activeClients.map((c) => {
              const t = byClient[c.id] ?? {
                hours: 0, hourlyTotal: 0, taskCharges: 0, fixedTotal: 0, subtotal: 0, gst: 0, total: 0,
              };
              return (
                <tr key={c.id}>
                  <td>
                    <div className="client-row">
                      <span className="dot" style={{ background: c.color }} />
                      <span style={{ fontWeight: 600 }}>{c.name}</span>
                    </div>
                  </td>
                  <td>
                    <span className="badge" style={{ background: c.color + "18", color: c.color }}>
                      {c.type.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="right">{t.hours.toFixed(1)}</td>
                  <td className="right">{fmt(t.hourlyTotal)}</td>
                  <td className="right">{fmt(t.taskCharges)}</td>
                  <td className="right">{fmt(t.fixedTotal)}</td>
                  <td className="right">{fmt(t.subtotal)}</td>
                  <td className="right">{fmt(t.gst)}</td>
                  <td className="right total-cell">{fmt(t.total)}</td>
                  <td className="right">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleGenerateInvoice(c.id)}
                      disabled={!canInvoiceClient(c, entries, rangeStartDate, rangeEndDate)}
                    >
                      Invoice ↗
                    </button>
                  </td>
                </tr>
              );
            })}
            <tr className="grand-total-row">
              <td style={{ fontWeight: 700, fontSize: 16 }}>Grand Total</td>
              <td />
              <td className="right total-cell">{totalHours.toFixed(1)}</td>
              <td className="right total-cell">{fmt(totalHourlyCharges)}</td>
              <td className="right total-cell">{fmt(totalTaskCharges)}</td>
              <td className="right total-cell">{fmt(totalFixedCharges)}</td>
              <td className="right total-cell">{fmt(grandSubtotal)}</td>
              <td className="right total-cell">{fmt(grandGST)}</td>
              <td className="right grand-total-value">{fmt(grandTotal)}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
